import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Building2, ChevronsUpDown } from "lucide-react";
import { destinoAlCambiarObra, useObraActiva } from "@/app/obra-activa";
import { useMe } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { recordarObraActiva } from "@/lib/obra-activa";
import { useObras } from "@/lib/obras";

// Valor centinela de la opción «Todas las obras…»: no es una obra, lleva al selector /obras.
const TODAS = "__todas__";
// Valor de la opción «Elige una obra»: el hueco del desplegable cuando no hay obra activa.
const NINGUNA = "";

/**
 * ObraSwitcher es el conmutador de obra de la cabecera (F7 #160/#187, #252). Muestra la obra
 * activa —la de la ruta o, fuera del árbol de la obra, la recordada (useObraActiva)— y, cuando
 * hay a dónde saltar (varias obras propias, o un admin que las gestiona todas), despliega el
 * resto más la opción «Todas las obras…» para volver al selector.
 *
 * #252: también se muestra en las rutas globales (usuarios, gestión de obras, sincronización,
 * selector), porque la obra activa se recuerda aunque la ruta no cuelgue de ella; sin obra
 * recordada válida ofrece «Elige una obra». Elegir otra obra conserva la sección en la que se
 * está (`/obras/1/lotos` → `/obras/2/lotos`, un detalle cae al listado de su sección) y desde
 * una ruta global permanece en la ruta: solo cambia la obra activa, y con ella la barra.
 *
 * #240: el conmutador se lee como un control, no como información, así que lleva delante un
 * rótulo fijo —«Obra seleccionada:» o «Sin obra seleccionada» cuando no hay ninguna válida—.
 * El nombre sigue apareciendo una sola vez: dentro del control (o del texto, en la variante
 * estática). Equivocarse de obra es un fallo de seguridad, y esto lo hace evidente siempre.
 *
 * El admin global puede estar en una obra que aún no figura en su /me (p. ej. recién creada):
 * el guard de ruta ya validó el acceso con el rol efectivo «ADMIN», así que el conmutador
 * recupera el nombre de la colección de obras (que el admin sí lee) en lugar de ocultarse. Si
 * ni así se resuelve, degrada a «Elige una obra» (D7 ronda 2): solo devuelve `null` mientras la
 * colección está cargando, o en la variante estática sin obra (donde no hay nada que ofrecer).
 */
type ObraSwitcherProps = {
  /** En escritorio apila rótulo y control para encajar en la barra lateral. */
  enBarraLateral?: boolean;
};

export function ObraSwitcher({ enBarraLateral = false }: ObraSwitcherProps) {
  const navigate = useNavigate();
  // La ruta actual decide el destino al cambiar de obra (misma sección en la obra nueva).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: me } = useMe();
  const obraActiva = useObraActiva();

  const enMe = me?.obras.find((o) => o.id_obra === obraActiva);
  // Solo el admin puede llegar a una obra que no está en su /me; en ese caso (y solo entonces)
  // se pide la colección para conocer su nombre. El resto de usuarios siempre tienen su obra.
  const faltaEnMe = me != null && obraActiva !== undefined && !enMe && me.es_admin;
  const coleccion = useObras({ enabled: faltaEnMe });
  const enColeccion = coleccion.data?.find((o) => o.id_obra === obraActiva);

  if (!me) {
    return null;
  }

  const nombre = enMe?.nombre ?? enColeccion?.nombre;
  const codigo = enMe?.codigo_obra ?? enColeccion?.codigo_obra ?? "";
  if (obraActiva !== undefined && nombre === undefined && coleccion.isLoading) {
    // Nombre aún por llegar: no se parpadea un hueco mientras la colección está en vuelo.
    return null;
  }
  // D7 (ronda 2): si la colección ya respondió y aun así no hay nombre (obra borrada, o que el
  // admin dejó de ver), NO se devuelve `null`: se degrada a la variante «Elige una obra». Desde
  // D4 el conmutador es la única salida al selector, y esconderlo dejaría al usuario encerrado.
  const hayObra = obraActiva !== undefined && nombre !== undefined;

  // Con una sola obra propia no hay a dónde saltar: se muestra estática, sin desplegable.
  const mostrarDesplegable = me.es_admin || me.obras.length > 1;
  if (!mostrarDesplegable) {
    if (!hayObra) {
      // Una sola obra y sin obra activa es un estado transitorio (el selector auto-entra).
      // Aquí no hay desplegable ni «Todas las obras…» que preservar (no hay a dónde saltar),
      // así que no se anuncia un conmutador vacío: es el único caso de `null` fuera de la
      // carga, y no encierra a nadie.
      return null;
    }
    return (
      <span
        // El tope de escritorio crece respecto al del desplegable (max-w-56) porque aquí el
        // rótulo comparte caja con el nombre: con 224px se truncaba el nombre, que es el dato.
        className={cn(
          "flex w-full items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 font-medium text-sm text-white md:inline-flex md:w-auto md:max-w-88",
          enBarraLateral && "md:w-full md:max-w-none",
        )}
        title={`${nombre} · ${codigo}`}
      >
        <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        {/* #240 (D1): aquí no hay control que lleve el nombre, así que el rótulo y el nombre son
            un mismo texto visible —y accesible: es el único portador del nombre—. */}
        <span className="truncate">Obra seleccionada: {nombre}</span>
      </span>
    );
  }

  // Opciones: las obras del usuario y, si la activa no figura (admin no-miembro), ella misma
  // al frente para que se muestre seleccionada; y siempre «Todas las obras…» al final.
  const opciones =
    hayObra && !enMe
      ? [{ id_obra: obraActiva, codigo_obra: codigo, nombre }, ...me.obras]
      : me.obras;
  const valor = hayObra ? String(obraActiva) : NINGUNA;

  return (
    <div
      className={cn(
        "flex w-full items-center gap-2 md:w-auto",
        enBarraLateral && "md:w-full md:flex-col md:items-stretch md:gap-1.5",
      )}
    >
      {/* #240 (D1/D5): el rótulo va delante del control y el nombre lo lleva el propio <select>
          (su opción seleccionada), así que aparece una sola vez. El rótulo se oculta al árbol de
          accesibilidad para no duplicarlo: el conmutador ya se anuncia como «Obra activa: X». */}
      <span aria-hidden="true" className="shrink-0 text-slate-300 text-sm">
        {hayObra ? "Obra seleccionada:" : "Sin obra seleccionada"}
      </span>
      <div
        className={cn(
          "relative flex min-w-0 flex-1 items-center md:max-w-56 md:flex-none",
          enBarraLateral && "md:w-full md:max-w-none",
        )}
      >
        <Building2
          className="pointer-events-none absolute left-3 h-4 w-4 shrink-0 text-white"
          aria-hidden="true"
        />
        <ChevronsUpDown
          className="pointer-events-none absolute right-2.5 h-4 w-4 shrink-0 text-white/70"
          aria-hidden="true"
        />
        <select
          aria-label={
            hayObra ? `Obra activa: ${nombre}. Cambiar de obra` : "Sin obra activa. Cambiar de obra"
          }
          value={valor}
          onChange={(event) => {
            const nuevo = event.target.value;
            if (nuevo === TODAS) {
              // `elegir` marca que el selector es el destino PEDIDO, no uno de paso (#265):
              // sin él, quien tiene una sola obra volvería al panel del que venía, porque el
              // selector auto-entra. Va en la URL, así que sobrevive a un recargado.
              void navigate({ to: "/obras", search: { elegir: true } });
              return;
            }
            if (nuevo === NINGUNA || nuevo === valor) {
              return;
            }
            // Se recuerda antes de navegar: si el destino es la ruta global en la que ya
            // estamos, la barra y el conmutador se repintan por la suscripción, no por la ruta.
            const nueva = Number(nuevo);
            recordarObraActiva(nueva);
            const destino = destinoAlCambiarObra(pathname, nueva);
            // `to` se calcula en tiempo de ejecución a partir de la ruta actual, así que no
            // encaja en la unión literal de rutas que tipa navigate; el destino sí es una de ellas.
            void navigate(destino as Parameters<typeof navigate>[0]);
          }}
          className="h-10 w-full cursor-pointer appearance-none truncate rounded-lg border border-white/20 bg-white/10 py-2 pr-9 pl-9 font-medium text-sm text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-800"
        >
          {/* Sin obra activa el desplegable no puede aparecer en blanco: el hueco lo ocupa una
            opción no elegible, que no es una obra pero sí explica qué falta. */}
          {!hayObra && (
            <option value={NINGUNA} disabled className="text-slate-900">
              Elige una obra
            </option>
          )}
          {opciones.map((obra) => (
            // El desplegable nativo lo pinta el sistema (fondo claro), de ahí el texto oscuro.
            <option key={obra.id_obra} value={String(obra.id_obra)} className="text-slate-900">
              {obra.nombre} · {obra.codigo_obra}
            </option>
          ))}
          <option value={TODAS} className="text-slate-900">
            Todas las obras…
          </option>
        </select>
      </div>
    </div>
  );
}
